import React from 'react';
import type {ZoneDetectResult} from '../../../../types/patternRecognition';
import type {AIAnalysis, SignalDescription} from '../types';
import {ZONE_DESCRIPTIONS} from '../config/signalDescriptions';
import {
    calcKlineGap,
    formatPrice,
    formatRelativeTime,
    formatSignalTime,
    formatTimeTooltip,
    getSignalBorderClass,
    parseCreatedAt
} from '../utils/patternTime';
import {AiAnalysisReport} from './AiAnalysisReport';
import {SignalDescriptionBlock} from './SignalDescriptionBlock';

interface ZoneSignalCardProps {
    zone: ZoneDetectResult;
    selected: boolean;
    onSelect: () => void;
    onViewChart: () => void;
    viewChartActive: boolean;
    interval?: string;
    aiData?: AIAnalysis;
    aiLoading: boolean;
    aiError?: string | null;
    isExpanded: boolean;
    onAiClick: () => void;
    quickTradeEnabled: boolean;
    canUseAiAnalysis: boolean;
    isPocPage: boolean;
    validationError?: string | null;
    expiryReason?: string | null;
    onGoTrade?: (direction: 'FORWARD' | 'REVERSE') => void;
}

export const ZoneSignalCard: React.FC<ZoneSignalCardProps> = ({
                                                                  zone,
                                                                  selected,
                                                                  onSelect,
                                                                  onViewChart,
                                                                  viewChartActive,
                                                                  interval,
                                                                  aiData,
                                                                  aiLoading,
                                                                  aiError,
                                                                  isExpanded,
                                                                  onAiClick,
                                                                  quickTradeEnabled,
                                                                  canUseAiAnalysis,
                                                                  isPocPage,
                                                                  validationError,
                                                                  expiryReason,
                                                                  onGoTrade,
                                                              }) => {
    const isSupport = 'LOW' === zone.zoneType;
    const isResistance = 'HIGH' === zone.zoneType;
    const isRange = 'RANGE' === zone.zoneType;

    const zoneColor = isSupport ? 'var(--pt-bull)' : isResistance ? 'var(--pt-amber)' : 'var(--pt-blue)';
    const zoneBg = isSupport ? 'var(--pt-bull-dim)' : isResistance ? 'var(--pt-amber-dim)' : 'var(--pt-blue-dim)';
    const zoneLabel = isSupport ? '支撑区' : isResistance ? '阻力区' : '盘整箱体';
    const desc: SignalDescription | undefined = ZONE_DESCRIPTIONS[zone.zoneType as 'LOW' | 'HIGH' | 'RANGE'];

    const hasData = !!aiData;
    const recognizedAt = parseCreatedAt(zone.createdAt);
    const timeGap = calcKlineGap(zone.zoneEndTime, Date.now(), interval);
    const borderClass = getSignalBorderClass(timeGap);

    return (
        <div
            className={`pattern-card${selected ? ' selected' : ''}${borderClass ? ' ' + borderClass : ''}`}
            onClick={onSelect}
        >
            <div className="pc-header">
                <div className="pc-name">{zone.patternName}</div>
                <div style={{
                    fontSize: 10, fontFamily: 'var(--pt-font-mono)', fontWeight: 600,
                    padding: '2px 7px', borderRadius: 2, display: 'inline-block',
                    background: zoneBg, color: zoneColor,
                    border: `1px solid ${zoneColor}33`,
                }}>
                    {zoneLabel}
                </div>
            </div>
            <div className="pc-meta">
                <span>{interval ?? '1H'}</span>
                <div className="pc-conf">
                    <span>强度</span>
                    <div className="conf-bar">
                        <div
                            className={`conf-fill${1 === zone.direction ? ' bull' : -1 === zone.direction ? ' bear' : ' neutral'}`}
                            style={{width: `${zone.confidence}%`}}
                        />
                    </div>
                    <span style={{color: zoneColor}}>{zone.confidence}%</span>
                </div>
            </div>
            <div className="pc-time">
                <div>形成区间 {formatSignalTime(zone.zoneStartTime)} → {formatSignalTime(zone.zoneEndTime)}</div>
                <div style={{fontSize: 10, color: 'var(--pt-text-dim)'}}
                     title={null == recognizedAt ? undefined : formatTimeTooltip(recognizedAt)}>
                    识别于 {null == recognizedAt ? '--' : formatSignalTime(recognizedAt)}
                    {null == recognizedAt ? '' : ` · ${formatRelativeTime(recognizedAt)}`}
                </div>
            </div>
            <div className="pc-zone-prices">
                <span>核心: <span style={{color: zoneColor}}>{formatPrice(zone.zonePrice)}</span></span>
                <span>上界: <span style={{color: 'var(--pt-bull)'}}>{formatPrice(zone.zoneUpper)}</span></span>
                <span>下界: <span style={{color: 'var(--pt-bear)'}}>{formatPrice(zone.zoneLower)}</span></span>
            </div>
            <div className="pc-zone-stats">
                <span>触及 {zone.touchCount} 次</span>
                <span>跨度 {zone.candleSpan} 根K线</span>
            </div>
            {desc && <SignalDescriptionBlock description={desc}/>}
            <button
                type="button"
                className={`pc-view-chart-btn${viewChartActive ? ' active' : ''}`}
                onClick={(event) => {
                    event.stopPropagation();
                    onViewChart();
                }}
            >
                在图表中查看
            </button>
            {!isPocPage || hasData || zone.hasAiAnalysis ? (
                <div className="pc-ai-section">
                    <button
                        className={`pc-ai-btn${aiLoading ? ' loading' : ''}${hasData || zone.hasAiAnalysis ? ' has-data' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAiClick();
                        }}
                        disabled={aiLoading || !canUseAiAnalysis}
                        title={aiLoading ? 'AI 分析中...' : hasData ? '查看AI解读' : '触发AI分析'}
                    >
                        {aiLoading ? '⏳' : hasData ? '📋' : '🤖'}
                    </button>
                </div>
            ) : null}
            {isExpanded && aiData && (
                <AiAnalysisReport
                    aiData={aiData}
                    holdingPeriodText={aiData.holdingPeriodText}
                    onGoTrade={onGoTrade}
                    quickTradeEnabled={quickTradeEnabled}
                    validationError={validationError}
                    expiryReason={expiryReason}
                    signalDirection={zone.direction as 1 | -1 | 0}
                    signalConfidence={zone.confidence}
                />
            )}
            {isExpanded && aiLoading && <div className="pc-ai-report">AI 分析中...</div>}
            {isExpanded && !aiLoading && aiError && <div className="ai-qt-error">{aiError}</div>}
        </div>
    );
};
